import type { JsonRegistration, Registration, RegistrationGroupMove } from '@/types'
import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { getRegistrations } from '@/api/registration'
import { asSettled } from '@/lib/maybePromise'
import { createRegistrationDraft } from '@/lib/registration'
import { toStoredRegistration } from '@/lib/wire'
import { atomWithLocalStorage } from '@/pages/state'
import { loadedAtom } from '@/pages/state/loadedAtom'
import { validIdTokenAtom } from '@/pages/state/user'

export const adminBackgroundActionsRunningAtom = atom(false)
export const adminEventRegistrationsFetchedAtAtom = atomFamily((_eventId: string) => atom<Date | undefined>(undefined))
export const adminEventRegistrationsCursorAtom = atomFamily((_eventId: string) => atom<Date | undefined>(undefined))
export const adminRegistrationIdAtom = atomWithLocalStorage<string | undefined>('adminRegistrationId', undefined)

export const adminEventRegistrationsAtom = atomFamily((eventId: string) => {
  const remoteAtom = atom(async (get) => {
    const token = get(validIdTokenAtom)
    return token ? ((await getRegistrations(eventId, token)) ?? []) : []
  })
  const overrideAtom = atom<Registration[] | undefined>(undefined)
  // A refetch after a refreshed id token never suspends the results page (loadedAtom).
  const loadedListAtom = loadedAtom(remoteAtom)
  return atom(
    (get) => asSettled(get(overrideAtom) ?? get(loadedListAtom)),
    (_get, set, value: Registration[] | ((previous: Registration[]) => Registration[])) =>
      set(overrideAtom, (current) => {
        if (typeof value !== 'function') return value
        if (!current) throw new Error('Cannot update registrations before they have loaded')
        return value(current)
      })
  )
})

/** Local-only commands layered over the WebSocket-backed server snapshot. */
export const adminPendingRegistrationGroupMovesAtom = atomFamily((_eventId: string) =>
  atom<RegistrationGroupMove[]>([])
)

export const createAdminNewRegistration = (): Registration => createRegistrationDraft('admin')
export const adminNewRegistrationAtom = atomWithLocalStorage<Registration | undefined, JsonRegistration>(
  'adminNewRegistration',
  createAdminNewRegistration(),
  { revive: toStoredRegistration }
)
